import React, { useState, useEffect, useRef } from 'react';
import '../CSS/ContributorContract.css';
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { BASE_API_URL } from '../constants';



const Page = ({ children, pageNumber }) => {
    const FOOTER_CONTENT = "Vithal Visions Private Limited | Daya House, Khairlanji Road, TIRORA, Tirora, Tirora, Gondia-441911 | Legal@cinevestor.in | vithalvision.in | DPIIT: DIPP188934 | CIN: U59141PN2025PTC237150";
    // Simplified Header for the template
    const Header = (
        <div className="header-bar">
            <div className="logo-section">
                {/* Logos as seen on the source PDF */}
                <span> 7 L</span>
                <span style={{ fontWeight: 'bold' }}>VITHAL VISIONS</span>
                {pageNumber === 1 && <span style={{ marginLeft: '20px' }}>Cinevestor</span>}
            </div>
        </div>
    );

    return (
        <div className="a4-page">
            {Header}
            <div className="page-content">{children}</div>
            <div className="footer-bar">
                <p className="footer-text">{FOOTER_CONTENT}</p>
                <p className="page-number">Page {pageNumber}</p>
            </div>
        </div>
    );
};

// Helper function to render data or a blank line
const renderField = (value, width = '150px') => (
    <span className="dynamic-field">
        {value || <span className="blank-line" style={{ width }}></span>}
    </span>
);

// --- MAIN CONTRACT TEMPLATE ---
const ContributorContract = () => {
    const [data, setData] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const pdfRef = useRef();


    const handleDownloadPDF = async () => {
        const input = pdfRef.current;
        if (!input) return;

        // Capture the content using html2canvas
        const canvas = await html2canvas(input, { scale: 2 });
        const imgData = canvas.toDataURL("image/png");

        // Create jsPDF instance
        const pdf = new jsPDF("p", "mm", "a4"); // portrait, mm units, A4

        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        let heightLeft = pdfHeight;
        let position = 0;

        // Add first page
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
        heightLeft -= pdf.internal.pageSize.getHeight();

        // Add additional pages if content overflows
        while (heightLeft > 0) {
            position = heightLeft - pdfHeight;
            pdf.addPage();
            pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
            heightLeft -= pdf.internal.pageSize.getHeight();
        }

        // Save/export the PDF
        pdf.save("ExportedFile.pdf");
    };


    useEffect(() => {
        const userId = sessionStorage.getItem("userId");
        if (!userId) return;

        // Fetch contracts
        fetch(`${BASE_API_URL}/api/contracts/${userId}`)
            .then(res => {
                if (!res.ok) throw new Error("Failed to fetch contracts");
                return res.json();
            })
            .then(contractData => {
                // Suppose backend returns first contract details
                const firstContract = contractData.contracts?.[0] || {};
                setData({
                    agreementDate: firstContract.agreementDate || '2025-09-30',
                    contributorName: firstContract.name || 'John Doe',
                    aadhaarNo: firstContract.aadhaarNo || 'XXXX XXXX 1234',
                    panNo: firstContract.panNo || 'ABCDE1234F',
                    contactEmail: firstContract.email || 'john.doe@example.com',
                    mobileNo: firstContract.mobile || '+91 98765 43210',
                    verifiedBankDetails: firstContract.bankDetails || 'AXIS BANK: A/C 9876543210',
                    contributionAmount: firstContract.amount || '5,00,000',
                    // contributionAmountInWords: firstContract.contributionAmountInWords || 'Five Lakh Indian Rupees Only',
                    projectName: firstContract.title || 'The Cinevestor Project',
                    projectTimeline: firstContract.timeline || '12 Months',
                    // ... etc
                });
                setIsLoading(false);
            })
            .catch(err => console.error("Error fetching contracts:", err));
    }, []);


    if (isLoading) {
        return <div>Loading Contract Data...</div>;
    }

    return (
        //     <div
        //     ref={pdfRef}
        //     style={{
        //       width: "210mm", // A4 width
        //       minHeight: "297mm", // A4 height
        //       padding: "20px",
        //       backgroundColor: "#fff",
        //       color: "#000",
        //     }}
        //   >

        <div className="contract-template"
            ref={pdfRef}
            style={{
                width: "210mm", // A4 width
                minHeight: "297mm", // A4 height
                padding: "20px",
                backgroundColor: "#fff",
                color: "#000",
            }}>

            {/* ======================= PAGE 1 ======================= */}
            <Page pageNumber={1}>
                <h1 style={{ textAlign: 'center' }}>CINEVESTOR INDIVIDUAL CONTRIBUTOR AGREEMENT</h1>
                <p style={{ textAlign: 'center', marginBottom: '30px' }}>
                    (With Integrated Privacy Policy, Terms & Conditions, Risk Disclosure, Refund Policy, and Extended Annexures)
                </p>

                <p>**Agreement Date:** {renderField(data.agreementDate)}</p>

                <h2>1. Parties</h2>

                <h3>Cinevestor (Platform): Vithal Visions Private Limited</h3>
                <ul>
                    [cite_start]<li>**CIN:** U59141PN2025PTC237150 [cite: 11]</li>
                    [cite_start]<li>**GSTN:** 27AAKCV6810E1Z9 [cite: 13]</li>
                    <li>**Registered Address:** Daya House Khairlanji Road, Tirora, Dist. [cite_start]Gondia - 441911, Maharashtra, India [cite: 15, 16]</li>
                    [cite_start]<li>**Contact Email:** legal@cinevestor.in [cite: 17]</li>
                </ul>

                <h3>Contributor (Individual):</h3>
                <ul>
                    [cite_start]<li>**Full Name:** {renderField(data.contributorName, '300px')} [cite: 19]</li>
                    [cite_start]<li>**Aadhaar No. (Mandatory for eSign & KYC):** {renderField(data.aadhaarNo)} [cite: 20, 21]</li>
                    [cite_start]<li>**PAN No. (If applicable):** {renderField(data.panNo)} [cite: 22, 23]</li>
                    [cite_start]<li>**Contact Email:** {renderField(data.contactEmail, '300px')} [cite: 24, 25]</li>
                    [cite_start]<li>**Mobile No.:** {renderField(data.mobileNo)} [cite: 26]</li>
                    [cite_start]<li>**Verified Bank Details:** {renderField(data.verifiedBankDetails, '300px')} [cite: 27]</li>
                </ul>

                <h3 style={{ marginTop: '30px' }}>Contribution Details:</h3>
                <ul>
                    [cite_start]<li>**Contribution Amount:** Rs.{renderField(data.contributionAmount)} [cite: 28]</li>
                    {/* [cite_start]<li>(In words: {renderField(data.contributionAmountInWords, '400px')}) [cite: 32]</li> */}
                    [cite_start]<li>**Project Name (Film/Webseries):** {renderField(data.projectName, '300px')} [cite: 29]</li>
                    [cite_start]<li>**Project Timeline:** {renderField(data.projectTimeline, '300px')} [cite: 30, 31]</li>
                </ul>

                <div style={{ marginTop: '50px', fontSize: '10pt' }}>
                    [cite_start]<p>**Punam** [cite: 33]</p>
                    [cite_start]<p>Signed by: Vithal Visions Private Limited [cite: 37]</p>
                    [cite_start]<p>Reason: Company Approval by Vithal Visions Private Limited [cite: 38]</p>
                    [cite_start]<p>eSigned using DSC (digio.in) [cite: 39]</p>
                    [cite_start]<p>Date: 2025-09-29 18:41:20 JST (Example eSign Date) [cite: 39]</p>
                </div>
            </Page>

            {/* ======================= PAGE 2 ======================= */}
            <Page pageNumber={2}>
                <h2>2. [cite_start]Definitions & Interpretation [cite: 44]</h2>
                <table className="contract-table">
                    <thead>
                        <tr>
                            <th>Term</th>
                            <th>Meaning</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>**Project**</td>
                            [cite_start]<td>Film/Webseries titled ({renderField(data.projectName, '200px')}) including script, marketing, and distribution (Annexure 1). [cite: 46]</td>
                        </tr>
                        <tr>
                            <td>**Net Profits**</td>
                            [cite_start]<td>Total Revenue minus (Production + Marketing + 15% Platform Fee) per Annexure 2. [cite: 46]</td>
                        </tr>
                        <tr>
                            <td>**Material Breach**</td>
                            [cite_start]<td>Violations as per Clauses 4, 7, or 9. [cite: 46]</td>
                        </tr>
                        <tr>
                            <td>**Force Majeure**</td>
                            [cite_start]<td>Events exempting liabilities (Annexure 6). [cite: 46]</td>
                        </tr>
                        <tr>
                            <td>**Contribution Amount**</td>
                            [cite_start]<td>{renderField(data.contributionAmount, '200px')} by Contributor. [cite: 46]</td>
                        </tr>
                    </tbody>
                </table>

                <p style={{ marginTop: '50px' }}>*(Content continues below...)*</p>
            </Page>

            {/* ======================= PAGE 3 ======================= */}
            <Page pageNumber={3}>
                <h2>3. [cite_start]Funding Structure & Utilization [cite: 54]</h2>
                <table className="contract-table">
                    <thead>
                        <tr>
                            <th>Component</th>
                            <th>% Allocation</th>
                            <th>Usage Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>**Production Budget**</td>
                            <td>70%</td>
                            [cite_start]<td>Scripting, casting, shooting, editing, post-production, VFX. [cite: 55]</td>
                        </tr>
                        <tr>
                            <td>**Marketing Budget**</td>
                            <td>20%</td>
                            [cite_start]<td>Digital marketing, PR, festival entries, promotional events. [cite: 55]</td>
                        </tr>
                        <tr>
                            <td>**Contingency Fund**</td>
                            <td>10%</td>
                            [cite_start]<td>Delays, reshoots, legal clearances, unforeseen expenses. [cite: 55]</td>
                        </tr>
                    </tbody>
                </table>

                <h2>4. [cite_start]Profit Sharing [cite: 57]</h2>
                <ul>
                    [cite_start]<li>Contributor receives up to **25% share of Net Profits** based on their Contribution Tier. [cite: 58]</li>
                    [cite_start]<li>Profits are paid in tranches: the first within **12 months of release**; the final settlement by **18 months**. [cite: 59]</li>
                    [cite_start]<li>Detailed methodology and sample calculation are provided in Annexure 2. [cite: 60]</li>
                </ul>
            </Page>

            {/* ======================= PAGE 4 ======================= */}
            <Page pageNumber={4}>
                <h2>5. [cite_start]Contributor Verification & Digital Execution [cite: 68]</h2>
                <ul>
                    [cite_start]<li>**Aadhaar, PAN, and bank details** are verified via the Cinevestor API KYC process. [cite: 69]</li>
                    [cite_start]<li>This contract is signed digitally using **Aadhaar-based Biometric eSign** as detailed in Annexure 7. [cite: 70]</li>
                    [cite_start]<li>The agreement becomes effective only upon successful completion of the eSign process. [cite: 71]</li>
                </ul>

                <h2>6. [cite_start]Privacy Policy (Annexure 3) [cite: 72]</h2>
                <ul>
                    [cite_start]<li>**Personal Data Collected:** Aadhaar, PAN, bank account, email, mobile, IP address, and cookies. [cite: 73]</li>
                    [cite_start]<li>**Purpose of Use:** KYC verification, fund transfers, legal compliance, and platform analytics. [cite: 74]</li>
                    [cite_start]<li>**Security Measures:** AES-256 encryption, SSL/TLS protocols. [cite: 75]</li>
                    [cite_start]<li>**Retention Period:** 5 years following project completion, or as required by law. [cite: 76]</li>
                </ul>

                <h2>7. [cite_start]Terms, Conditions, & Obligations (Annexure 4) [cite: 77]</h2>
                <ul>
                    [cite_start]<li>Cinevestor acts solely as a platform; the contributor holds **no ownership or creative influence** over the project. [cite: 78]</li>
                    [cite_start]<li>**Liability is capped** at 25,000 or the contributed amount, whichever is lower. [cite: 79, 293, 497]</li>
                    [cite_start]<li>Detailed refund conditions, intellectual property rights, and user responsibilities are outlined. [cite: 80]</li>
                </ul>
            </Page>

            {/* ======================= PAGE 5 ======================= */}
            <Page pageNumber={5}>
                <h2>8. [cite_start]Disclaimers & Refund Policy (Annexure 5) [cite: 89]</h2>
                <ul>
                    [cite_start]<li>Contributions are **voluntary** and are **not investments with guaranteed returns**. [cite: 90, 288]</li>
                    [cite_start]<li>Refunds are only available **before the commencement of production**. [cite: 90]</li>
                    [cite_start]<li>Once production starts, **no refunds** will be issued unless otherwise approved under exceptional circumstances. [cite: 91, 544]</li>
                    [cite_start]<li>A detailed refund process is included. [cite: 92]</li>
                </ul>

                <h2>9. [cite_start]Dispute Resolution & Jurisdiction [cite: 93]</h2>
                <ul>
                    [cite_start]<li>This Agreement is governed by **Indian law**. [cite: 94]</li>
                    [cite_start]<li>It includes a mandatory **arbitration clause** with the **exclusive jurisdiction** of the **Gondia District courts in Maharashtra**. [cite: 95, 294, 499]</li>
                    [cite_start]<li>Division of costs will be determined by the arbitration or court ruling. [cite: 96]</li>
                </ul>

                <h2>10. [cite_start]Force Majeure (Annexure 6) [cite: 97]</h2>
                <ul>
                    [cite_start]<li>**Qualifying Events:** Natural disasters, government bans, epidemics, or payment gateway failures exceeding 7 consecutive days. [cite: 98, 310, 311]</li>
                    [cite_start]<li>**Effect:** Suspends the obligations of Cinevestor and the project for the duration of the event. [cite: 99]</li>
                </ul>

                <h2>11. [cite_start]Execution & eSign Protocol (Annexure 7) [cite: 100]</h2>
                <ol>
                    [cite_start]<li>Contributor verifies their Aadhaar via OTP. [cite: 101, 321]</li>
                    [cite_start]<li>Contributor completes the signing process using Biometric/Aadhaar eSign. [cite: 102, 322]</li>
                    [cite_start]<li>Proof of this digital signature is stored securely. [cite: 103, 323]</li>
                </ol>
            </Page>

            {/* ======================= PAGE 6 ======================= */}
            <Page pageNumber={6}>
                <h2>12. [cite_start]Notices [cite: 111]</h2>
                <ul>
                    [cite_start]<li>All official communications will be conducted via the registered email and physical addresses provided. [cite: 113]</li>
                    [cite_start]<li>Parties are required to provide prompt notification of any changes to their contact information. [cite: 114]</li>
                </ul>

                <h2>13. [cite_start]Entire Agreement [cite: 115]</h2>
                <ul>
                    [cite_start]<li>This document supersedes all prior agreements and understandings. [cite: 116]</li>
                    [cite_start]<li>Any amendments must be made via a written, eSigned addendum agreed to by both parties. [cite: 117]</li>
                </ul>

                [cite_start]<h2 style={{ marginTop: '50px' }}>SIGNATORY PAGE [cite: 118]</h2>

                <div className="signature-block">
                    [cite_start]<h3>For Vithal Visions Private Limited (Cinevestor.in): [cite: 119]</h3>
                    [cite_start]<p>Signature: {renderField(null, '300px')} [cite: 120]</p>
                    [cite_start]<p>Name: **Kamal Meshram** [cite: 121]</p>
                    [cite_start]<p>Title: **Founder** [cite: 122]</p>
                    [cite_start]<p>Date: {renderField(null, '300px')} [cite: 123, 124]</p>
                </div>

                <div className="signature-block" style={{ marginTop: '50px' }}>
                    [cite_start]<h3>For Contributor (Individual): [cite: 125]</h3>
                    [cite_start]<p>Signature: {renderField(null, '300px')} [cite: 126]</p>
                    [cite_start]<p>Name: {renderField(data.contributorName, '300px')} [cite: 127]</p>
                    [cite_start]<p>Date: {renderField(null, '300px')} [cite: 128]</p>
                </div>
            </Page>

            {/* ======================= PAGE 7: ANNEXURES START ======================= */}
            <Page pageNumber={7}>
                [cite_start]<h1>ANNEXURES [cite: 136]</h1>

                [cite_start]<h2>ANNEXURE 1: PROJECT DETAILS [cite: 137]</h2>
                <ol>
                    [cite_start]<li>**Project Title:** {renderField(data.projectName, '300px')} [cite: 139, 140]</li>
                    [cite_start]<li>**Synopsis & Script Summary:** {renderField(null, '400px')} (Detailed narrative or story outline, refer to project detail page) [cite: 141]</li>
                    [cite_start]<li>**Project Genre:** {renderField(data.projectGenre)} [cite: 142]</li>
                    [cite_start]<li>**Budget:** {renderField(data.projectBudget)} [cite: 143]</li>
                    [cite_start]<li>**Production Schedule:** [cite: 144]
                        <ul>
                            [cite_start]<li>Pre-production Start: {renderField(data.preProductionStart)} [cite: 145, 146]</li>
                            [cite_start]<li>Principal Photography: {renderField(data.principalPhotography)} to {renderField(null, '150px')} [cite: 147, 150]</li>
                            [cite_start]<li>Post-production: {renderField(data.postProduction)} to {renderField(null, '150px')} [cite: 148, 152]</li>
                        </ul>
                    </li>
                    [cite_start]<li>**Key Personnel:** [cite: 149]
                        <ul>
                            [cite_start]<li>Director: {renderField(data.directorName)} [cite: 153]</li>
                            [cite_start]<li>Producer: {renderField(data.producerName)} [cite: 154]</li>
                            [cite_start]<li>Lead Cast: {renderField(data.leadCast, '300px')} [cite: 155]</li>
                            [cite_start]<li>Technical Heads (Cinematography, Editing, etc.): {renderField(data.technicalHeads, '300px')} [cite: 156]</li>
                        </ul>
                    </li>
                </ol>

                [cite_start]<h2>ANNEXURE 2: PROFIT CALCULATION METHODOLOGY [cite: 157]</h2>
                [cite_start]<p>This annexure provides an example of how Net Profits are calculated. [cite: 158]</p>

                [cite_start]<h3>Assumptions (Example Scenario): [cite: 159]</h3>
                <ul>
                    [cite_start]<li>Total Gross Revenue: 40,00,000 [cite: 160]</li>
                    [cite_start]<li>Production Cost: 30,00,000 [cite: 161]</li>
                    [cite_start]<li>Marketing & Promotion Expenses: 5,00,000 [cite: 162]</li>
                    [cite_start]<li>Platform Fee (15% of Gross Revenue): 6,00,000 (15% of 40,00,000) [cite: 163]</li>
                    [cite_start]<li>Contributor's Profit Share: 25% (based on their contribution tier) [cite: 163]</li>
                </ul>

                [cite_start]<h3>Step 1: Calculate Net Profits [cite: 164]</h3>
                [cite_start]<p>$$Net~Profits=Gross~Revenue-(Production~Cost+Marketing~Expense+Platform~Fee)$$ [cite: 165]</p>
                [cite_start]<p>$$Net~Profits=40,00,000-(\text{""}30,00,000+\text{""}5,00,000+\text{""}6,00,000)$$ [cite: 166]</p>
                [cite_start]<p>$$Net~Profits=\text{""}40,000,000-\text{""}41,00,000=-\text{""}1,00,000$$ [cite: 167]</p>
                [cite_start]<p>In this scenario, the result is a loss of 1,00,000, meaning there is no distributable profit. [cite: 168]</p>
            </Page>

            {/* ======================= PAGE 8: ANNEXURE 2 & 3 ======================= */}
            <Page pageNumber={8}>
                [cite_start]<h3>Step 2: If Net Profits Were Positive (Hypothetical Scenario) [cite: 177]</h3>
                [cite_start]<p>Suppose the Gross Revenue was 50,00,000: [cite: 178]</p>
                [cite_start]<p>$$Net~Profits=50,00,000-(\text{""}30,00,000+\text{""}5,00,000+\text{""}7,50,000)=\text{""}7,50,000$$ [cite: 179]</p>
                [cite_start]<p>Contributor's Share (25% of Net Profits): [cite: 180]</p>
                [cite_start]<p>$$\text{""}7,50,000 \times 25\%=\text{""}1,87,500$$ [cite: 181]</p>

                [cite_start]<h3>Step 3: Payment Timeline [cite: 182]</h3>
                <ul>
                    [cite_start]<li>The first tranche of the Contributor's share is paid within **12 months of the project's release**. [cite: 183]</li>
                    [cite_start]<li>The final settlement, including any additional profits, is completed within **18 months** of the contribution date. [cite: 184]</li>
                </ul>

                [cite_start]<h2>ANNEXURE 3: PRIVACY POLICY [cite: 185]</h2>
                <p>Last Updated: 11/06/2025 | [cite_start]Effective for: Contributors, Filmmakers, Talent/Technicians [cite: 186]</p>

                <h3>1. [cite_start]Information We Collect [cite: 187]</h3>
                [cite_start]<h4>For All Users: [cite: 189]</h4>
                <ul>
                    [cite_start]<li>**Personal Data:** Name, email, phone, address, PAN/GST (for payouts). [cite: 190, 405]</li>
                    [cite_start]<li>**Professional Data:** (Filmmakers/Talent) Portfolio, credentials, bank details. [cite: 191, 406]</li>
                    [cite_start]<li>**Financial Data:** (Contributors) Contribution amount, payment method (via secure gateways). [cite: 192, 407]</li>
                    [cite_start]<li>**Technical Data:** IP address, device type, cookies (see Section 4). [cite: 193, 408]</li>
                </ul>
                [cite_start]<h4>Special Categories: [cite: 194]</h4>
                <ul>
                    [cite_start]<li>**Sensitive Data:** Government IDs (for KYC) -- stored encrypted, used only for compliance. [cite: 195, 410]</li>
                </ul>
            </Page>

            {/* ======================= PAGE 9: ANNEXURE 3 (continued) ======================= */}
            <Page pageNumber={9}>
                <h3>2. [cite_start]How We Use Your Data [cite: 202]</h3>
                <table className="contract-table">
                    <thead>
                        <tr>
                            <th>Purpose</th>
                            <th>Legal Basis</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Facilitate contributions/projects</td>
                            [cite_start]<td>Contractual necessity [cite: 204, 414]</td>
                        </tr>
                        <tr>
                            <td>Profit-sharing calculations</td>
                            [cite_start]<td>Legitimate business interest [cite: 204, 415]</td>
                        </tr>
                        <tr>
                            <td>Marketing (e.g., new projects)</td>
                            [cite_start]<td>Consent (opt-in required) [cite: 204, 418]</td>
                        </tr>
                        <tr>
                            <td>Compliance (tax/fraud checks)</td>
                            [cite_start]<td>Legal obligation [cite: 204, 418]</td>
                        </tr>
                    </tbody>
                </table>
                [cite_start]<p>For Filmmakers/Talent: Profile display on Cinevestor platform and payment processing. [cite: 205, 419]</p>

                <h3>3. [cite_start]Data Sharing & Disclosure [cite: 206]</h3>
                <ul>
                    [cite_start]<li>With Filmmaker Teams: Contributor contact details (only for project updates). [cite: 207, 428]</li>
                    [cite_start]<li>Third Parties: Payment processors (Razorpay/Stripe), Analytics (Google Analytics anonymized data). [cite: 208, 429, 430]</li>
                    [cite_start]<li>Legal Disclosures: If required by courts/law enforcement. [cite: 209, 431]</li>
                    [cite_start]<li>We **do not sell data to advertisers**. [cite: 211, 432]</li>
                </ul>

                <h3>4. [cite_start]Cookies & Tracking [cite: 212]</h3>
                <ul>
                    [cite_start]<li>**Essential Cookies:** For login sessions and security. [cite: 213, 434]</li>
                    [cite_start]<li>**Analytical Cookies:** For page visits and demographics (opt-out via browser settings). [cite: 214, 435]</li>
                    [cite_start]<li>**Marketing Cookies:** For retargeting ads (disabled by default). [cite: 214, 436]</li>
                </ul>

                <h3>5. [cite_start]User Rights [cite: 215]</h3>
                <p>You can access/correct your data via your dashboard, request deletion (except for legal/financial records), withdraw marketing consent, and port your data by contacting dpo@cinevestor.in. [cite_start]We will respond within **30 days**. [cite: 216, 217, 438, 440, 441, 442]</p>

                <h3>6. [cite_start]Data Security [cite: 218]</h3>
                [cite_start]<p>We use **AES-256 encryption**, role-based access controls, and have a **72-hour breach notification protocol**. [cite: 219, 444, 445, 446]</p>
            </Page>

            {/* ======================= PAGE 10: ANNEXURE 3 (end) & 4 (start) ======================= */}
            <Page pageNumber={10}>
                <h3>7. [cite_start]Retention Period [cite: 226]</h3>
                <table className="contract-table">
                    <thead>
                        <tr>
                            <th>Data Type</th>
                            <th>Retention Period</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Financial records</td>
                            [cite_start]<td>7 years (tax compliance) [cite: 227, 449]</td>
                        </tr>
                        <tr>
                            <td>User profiles</td>
                            [cite_start]<td>3 years post-inactivity [cite: 227, 450]</td>
                        </tr>
                        <tr>
                            <td>Marketing consents</td>
                            [cite_start]<td>Until revoked [cite: 227, 451]</td>
                        </tr>
                    </tbody>
                </table>

                <h3>8. [cite_start]Children's Privacy [cite: 229]</h3>
                <p>Our platform is strictly for users **18 years and older**. [cite_start]We do not knowingly collect data from minors. [cite: 230, 453]</p>

                <h3>9. [cite_start]Updates & Grievances [cite: 231]</h3>
                <p>Policy changes will be notified via email/dashboard. [cite_start]For complaints, contact **legal@cinevestor.in**. [cite: 232, 233, 455, 457]</p>

                <h3>10. [cite_start]Jurisdiction [cite: 234]</h3>
                <p>Governed by **Indian law (IT Act, 2000)**. [cite_start]Disputes shall be resolved in **Gondia, Maharashtra courts**. [cite: 235, 469]</p>

                [cite_start]<h2>ANNEXURE 4: TERMS & CONDITIONS [cite: 236]</h2>
                <p>Last Updated: 27/06/2025 | [cite_start]Governing Entity: Vithal Visions Private Limited [cite: 237]</p>

                <h3>1. [cite_start]Definitions [cite: 238]</h3>
                <ul>
                    [cite_start]<li>"Platform": Cinevestor.in website and associated services. [cite: 239, 334]</li>
                    [cite_start]<li>"Contributor": Individual/organization providing funds. [cite: 240, 335]</li>
                    [cite_start]<li>"Filmmaker": Project owner registered on the platform. [cite: 241, 336]</li>
                    [cite_start]<li>"Talent": Actors/technicians hired through the platform. [cite: 336]</li>
                </ul>
            </Page>

            {/* ======================= PAGE 11: ANNEXURE 4 (continued) ======================= */}
            <Page pageNumber={11}>
                <h3>2. [cite_start]Eligibility & Registration [cite: 250, 337]</h3>
                [cite_start]<p>2.1 Users must: [cite: 251, 338]</p>
                <ul>
                    [cite_start]<li>Be **18+ years old**. [cite: 251, 339]</li>
                    [cite_start]<li>Provide **accurate KYC details** (if contributing 20,000+). [cite: 252, 340]</li>
                    [cite_start]<li>Maintain account confidentiality. [cite: 252, 341]</li>
                </ul>
                [cite_start]<p>2.2 Prohibited users: [cite: 253, 342]</p>
                <ul>
                    [cite_start]<li>Those previously banned for fraud. [cite: 253, 343]</li>
                    [cite_start]<li>Entities on **RBI/SEBI watchlists**. [cite: 253, 344]</li>
                </ul>

                <h3>3. [cite_start]Contribution Terms [cite: 254, 353]</h3>
                [cite_start]<p>3.1 For Contributors: [cite: 255, 354]</p>
                <ul>
                    [cite_start]<li>Funds are **non-refundable** except under Section 5. [cite: 255, 355]</li>
                    [cite_start]<li>Profit-sharing (up to 25%) is **not guaranteed**. [cite: 255, 355]</li>
                    [cite_start]<li>Profit distribution timeline is **12-18 months**. [cite: 256, 355]</li>
                </ul>
                [cite_start]<p>3.2 For Filmmakers: [cite: 257, 356]</p>
                <ul>
                    [cite_start]<li>Must disclose project risks transparently. [cite: 257, 357]</li>
                    [cite_start]<li>Required to provide **quarterly progress updates**. [cite: 257, 358]</li>
                </ul>

                <h3>4. [cite_start]Intellectual Property [cite: 258, 359]</h3>
                <ul>
                    [cite_start]<li>Platform content is owned by Vithal Visions Pvt Ltd. [cite: 259, 360]</li>
                    [cite_start]<li>Project IP remains with the respective filmmakers. [cite: 259, 361]</li>
                    [cite_start]<li>User-generated content license grants Cinevestor royalty-free usage rights for promotion. [cite: 362]</li>
                </ul>

                <h3>5. [cite_start]Refund & Cancellation [cite: 260]</h3>
                <table className="contract-table">
                    <thead>
                        <tr>
                            <th>Scenario</th>
                            <th>Policy</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>No progress in 140 days</td>
                            [cite_start]<td>Full refund [cite: 261, 364]</td>
                        </tr>
                        <tr>
                            <td>Project cancellation</td>
                            [cite_start]<td>Pro-rata refund after deductions [cite: 261, 364]</td>
                        </tr>
                        <tr>
                            <td>Contributor withdrawal</td>
                            [cite_start]<td>No refund (funds locked) [cite: 261, 364]</td>
                        </tr>
                    </tbody>
                </table>
            </Page>

            {/* ======================= PAGE 12: ANNEXURE 4 (end) & 5 (start) ======================= */}
            <Page pageNumber={12}>
                <h3>6. [cite_start]Dispute Resolution [cite: 262, 365]</h3>
                <ul>
                    [cite_start]<li>Mediation: Mandatory **30-day negotiation period**. [cite: 263, 366]</li>
                    [cite_start]<li>Arbitration: Final settlement in **Gondia, Maharashtra**. [cite: 264, 367]</li>
                    [cite_start]<li>Governing Law: **Indian Contract Act, 1872**. [cite: 265, 368]</li>
                </ul>

                <h3>7. [cite_start]Limitation of Liability [cite: 266, 369]</h3>
                [cite_start]<p>Cinevestor is not liable for project failures, profit losses, or technical outages beyond 72 hours. [cite: 267, 371, 372]</p>
                [cite_start]<p>Maximum Liability Cap: **25,000 or the contribution amount, whichever is lower.** [cite: 293, 497]</p>

                <h3>8. [cite_start]Termination Policy [cite: 276]</h3>
                <p>We may suspend accounts for fraudulent activity or violation of these terms. [cite_start]Users may delete accounts via their dashboard. [cite: 277, 376, 377, 379]</p>

                <h3>9. [cite_start]Amendments [cite: 278]</h3>
                <p>A **30-day notice** will be given for major changes. [cite_start]Continued use constitutes acceptance. [cite: 279, 381, 382]</p>

                <h3>10. [cite_start]Contact Information [cite: 280, 390]</h3>
                [cite_start]<p>Grievance Officer: {renderField(null, '200px')} [cite: 280, 281]</p>
                [cite_start]<p>Email: legal@cinevestor.in [cite: 283, 391]</p>
                [cite_start]<p>Address: Daya House, Khairlanji Road, Tiroda, Dist.Gondia-441911, Maharashtra [cite: 284, 392]</p>

                [cite_start]<h2>ANNEXURE 5: DISCLAIMER & REFUND POLICY [cite: 285]</h2>
                [cite_start]<h3>Disclaimer [cite: 286]</h3>
                [cite_start]<p>Last Updated: 27/06/2025 [cite: 287]</p>
                <p>1. [cite_start]**Nature of Platform:** Cinevestor.in is a **creative collaboration platform**, not a financial investment portal (SEBI/RBI exempt) or a guaranteed profit-generating scheme. [cite: 288, 476, 477, 478]</p>
                <p>2. **No Guarantees:** Profit sharing is not guaranteed. Project outcomes (budgets, release dates) are estimates only. [cite_start]Refunds are strictly limited. [cite: 289, 481, 482]</p>
                <p>3. [cite_start]**Third-Party Links:** We are not responsible for the content on third-party sites linked from our platform. [cite: 290, 488]</p>
                <p>4. [cite_start]**Intellectual Property:** All platform content is owned by Vithal Visions Pvt Ltd. Film rights remain with creators. [cite: 291, 490]</p>
            </Page>

            {/* ======================= PAGE 13: ANNEXURE 5 (continued) & 6 ======================= */}
            <Page pageNumber={13}>
                <p>5. **Limitation of Liability:** Cinevestor is not liable for project failures or financial losses. [cite_start]Our maximum liability is capped at **25,000 or the contribution amount, whichever is lower.** [cite: 292, 293, 497]</p>
                <p>6. [cite_start]**Jurisdiction:** Disputes are subject to the **exclusive jurisdiction of Gondia Courts, Maharashtra.** [cite: 294, 499]</p>

                [cite_start]<h3>Refund Policy [cite: 295, 515]</h3>
                [cite_start]<p>Last Updated: 27/06/2025 [cite: 296, 516]</p>

                [cite_start]<h4>Eligibility for Refund: [cite: 303, 519]</h4>
                <table className="contract-table">
                    <thead>
                        <tr>
                            <th>Scenario</th>
                            <th>Refund Eligibility</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>No progress on the project within **140 days**</td>
                            [cite_start]<td>Full refund [cite: 304, 523, 524]</td>
                        </tr>
                        <tr>
                            <td>Project is officially canceled</td>
                            [cite_start]<td>Pro-rata refund (after deductions) [cite: 304, 525]</td>
                        </tr>
                        <tr>
                            <td>Contributor voluntarily withdraws</td>
                            [cite_start]<td>No refund (funds are locked) [cite: 304, 526]</td>
                        </tr>
                    </tbody>
                </table>

                <h4>Processing Guidelines:</h4>
                <ul>
                    [cite_start]<li>Requests must be sent to **support@cinevestor.in**. [cite: 306, 528]</li>
                    [cite_start]<li>Approved refunds are processed within **15 working days**. [cite: 306, 532]</li>
                    [cite_start]<li>Platform fees $$(1.5\%+GST)$$ and payment gateway charges are **non-refundable**. [cite: 307, 533]</li>
                </ul>

                [cite_start]<h4>Non-Refundable Conditions: [cite: 308, 542]</h4>
                <ul>
                    [cite_start]<li>No refunds will be issued after funds are **committed to production**. [cite: 308, 544]</li>
                    [cite_start]<li>No refunds for delays due to **Force Majeure**. [cite: 308, 545]</li>
                    [cite_start]<li>If requested after **180 days** from contribution. [cite: 308, 546]</li>
                </ul>

                [cite_start]<h2>ANNEXURE 6: FORCE MAJEURE EVENTS [cite: 309]</h2>
                <ul>
                    [cite_start]<li>Natural disasters (earthquake, flood, cyclone, etc.) [cite: 310]</li>
                    [cite_start]<li>War, terrorism, civil commotion [cite: 310]</li>
                    [cite_start]<li>Pandemic or epidemic public health emergencies [cite: 311]</li>
                    [cite_start]<li>Government bans or interventions affecting project/distribution [cite: 311]</li>
                    [cite_start]<li>Payment gateway or banking failures exceeding 7 consecutive days [cite: 311]</li>
                    [cite_start]<li>Other uncontrollable circumstances mutually agreed upon [cite: 311]</li>
                </ul>
            </Page>

            {/* ======================= PAGE 14: ANNEXURE 7 & FOOTER (Extended T&C) ======================= */}
            <Page pageNumber={14}>
                [cite_start]<h2>ANNEXURE 7: EXECUTION & AADHAAR ESIGN [cite: 319]</h2>
                <ol>
                    [cite_start]<li>The Contributor shall verify their Aadhaar via an **OTP** sent to their registered phone number. [cite: 321]</li>
                    [cite_start]<li>The Contributor completes the **biometric Aadhaar eSign** through an authorized eSign provider integrated with the Platform. [cite: 322]</li>
                    [cite_start]<li>Cinevestor records the eSign as a **binding legal signature** under the Indian IT Act, 2000. [cite: 323]</li>
                    [cite_start]<li>Copies of the digitally signed agreement are provided to both the Contributor and Cinevestor for their records. [cite: 324]</li>
                </ol>

                [cite_start]<h2 style={{ marginTop: '50px' }}>FOOTER (Notice and Version Control) [cite: 325]</h2>
                [cite_start]<p>**Notice Address:** [cite: 326]</p>
                <p>Vithal Visions Private Limited, Daya house Khairlanji Road, Tirora, Dist. [cite_start]Gondia, Maharashtra, India-441911 [cite: 327]</p>
                [cite_start]<p>Email: legal@cinevestor.in [cite: 328]</p>
                [cite_start]<p>**Version Control:** [cite: 329]</p>
                <p>This Agreement supersedes all prior drafts and agreements. [cite_start]Last updated: {renderField('21/09/2025')} [cite: 330]</p>

                [cite_start]<h2 style={{ marginTop: '50px' }}>Terms and Conditions (Repeated/Extended) [cite: 331]</h2>
                <p>Last Updated: 27/06/2025 | [cite_start]Governing Entity: Vithal Visions Private Limited (CIN: U59141PN2025PTC237150) [cite: 332]</p>

                <h3>1. [cite_start]Definitions [cite: 333]</h3>
                <ul>
                    [cite_start]<li>"Platform": Cinevestor.in website and associated services [cite: 334]</li>
                    [cite_start]<li>"Contributor": Individual/organization providing funds [cite: 335]</li>
                    [cite_start]<li>"Filmmaker": Project owner registered on the platform [cite: 336]</li>
                    [cite_start]<li>"Talent": Actors/technicians hired through the platform [cite: 336]</li>
                </ul>

                <h3>2. [cite_start]Eligibility & Registration [cite: 337]</h3>
                [cite_start]<p>2.1 Users must: [cite: 338]</p>
                <ul>
                    [cite_start]<li>Be **18+ years old** [cite: 339]</li>
                    [cite_start]<li>Provide **accurate KYC details** (if contributing 20,000+) [cite: 340]</li>
                    [cite_start]<li>Maintain account confidentiality [cite: 341]</li>
                </ul>
            </Page>

            {/* ======================= PAGE 15: Extended T&C (continued) ======================= */}
            <Page pageNumber={15}>
                [cite_start]<p>2.2 Prohibited users: [cite: 342]</p>
                <ul>
                    [cite_start]<li>Those previously banned for fraud [cite: 343]</li>
                    [cite_start]<li>Entities on RBI/SEBI watchlists [cite: 344]</li>
                </ul>

                <h3>3. [cite_start]Contribution Terms [cite: 353]</h3>
                [cite_start]<p>3.1 For Contributors: [cite: 354]</p>
                <ul>
                    [cite_start]<li>Funds are **non-refundable** except under Section 5 [cite: 355]</li>
                    [cite_start]<li>Profit-sharing (up to 25%) is **not guaranteed** [cite: 355]</li>
                    [cite_start]<li>**12-18 month** profit distribution timeline [cite: 355]</li>
                </ul>
                [cite_start]<p>3.2 For Filmmakers: [cite: 356]</p>
                <ul>
                    [cite_start]<li>Must disclose project risks transparently [cite: 357]</li>
                    [cite_start]<li>Required to provide quarterly progress updates [cite: 358]</li>
                </ul>

                <h3>4. [cite_start]Intellectual Property [cite: 359]</h3>
                <ul>
                    [cite_start]<li>Platform content is owned by Vithal Visions Pvt Ltd [cite: 360]</li>
                    [cite_start]<li>Project IP remains with respective filmmakers [cite: 361]</li>
                    [cite_start]<li>User-generated content license grants Cinevestor **royalty-free usage rights for promotion** [cite: 362]</li>
                </ul>

                <h3>5. [cite_start]Refund & Cancellation [cite: 363]</h3>
                [cite_start]<p>Scenario, Policy, No progress in 140 days Full refund, Project cancellation Pro-rata refund after deductions, Contributor withdrawal No refund (funds locked) [cite: 364]</p>

                <h3>6. [cite_start]Dispute Resolution [cite: 365]</h3>
                <ul>
                    [cite_start]<li>**Mediation:** Mandatory 30-day negotiation period [cite: 366]</li>
                    [cite_start]<li>**Arbitration:** Final settlement in Gondia, Maharashtra [cite: 367]</li>
                    [cite_start]<li>**Governing Law:** Indian Contract Act, 1872 [cite: 368]</li>
                </ul>

                <h3>7. [cite_start]Limitation of Liability [cite: 369]</h3>
                [cite_start]<p>Cinevestor is not liable for: [cite: 370]</p>
                <ul>
                    [cite_start]<li>Project failures or profit losses [cite: 371]</li>
                    [cite_start]<li>Technical outages beyond 72 hours [cite: 372]</li>
                    [cite_start]<li>Third-party actions (payment gateways, filmmakers) [cite: 373]</li>
                </ul>
            </Page>

            {/* ======================= PAGE 16: Extended T&C (end) & Annexure Index ======================= */}
            <Page pageNumber={16}>
                <h3>8. [cite_start]Termination Policy [cite: 374]</h3>
                [cite_start]<p>8.1 We may suspend accounts for: [cite: 375]</p>
                <ul>
                    [cite_start]<li>Fraudulent activity [cite: 376]</li>
                    [cite_start]<li>Violation of these terms [cite: 377]</li>
                    [cite_start]<li>Regulatory requirements [cite: 378]</li>
                </ul>
                [cite_start]<p>8.2 Users may delete accounts via dashboard [cite: 379]</p>

                <h3>9. [cite_start]Amendments [cite: 380]</h3>
                <ul>
                    [cite_start]<li>30-day notice for major changes [cite: 381]</li>
                    [cite_start]<li>Continued use constitutes acceptance [cite: 382]</li>
                </ul>

                <h3>10. [cite_start]Contact Information [cite: 390]</h3>
                <ul>
                    [cite_start]<li>Email: legal@cinevestor.in [cite: 391]</li>
                    <li>Address: Daya House, Khairlanji Road, Tiroda, Dist. [cite_start]Gondia-441911, Maharashtra [cite: 392]</li>
                </ul>

                [cite_start]<h2 style={{ marginTop: '50px' }}>Annexures (Index/List) [cite: 393]</h2>
                <ul>
                    <li>A. [cite_start]List of Prohibited Activities [cite: 394]</li>
                    <li>B. [cite_start]State-wise Legal Addendums [cite: 395]</li>
                    <li>C. [cite_start]Fee Structure [cite: 396]</li>
                    <li>D. [cite_start]Data Processing Agreement [cite: 397]</li>
                    <li>E. [cite_start]Force Majeure [cite: 398]</li>
                    <li>F. [cite_start]Cookie Policy [cite: 399]</li>
                </ul>

                [cite_start]<h2 style={{ marginTop: '50px' }}>Privacy Policy (Repeated/Extended) [cite: 401]</h2>
                <p>Last Updated: 11/06/2025 | [cite_start]Effective for: Contributors, Filmmakers, Talent/Technicians [cite: 402]</p>
                <p>**1. [cite_start]Information We Collect** [cite: 403] (See Page 8/9 for details)
                    <br />
                    **2. [cite_start]How We Use Your Data** [cite: 411] (See Page 9 for details)</p>
            </Page>

            {/* ======================= PAGES 17-20: Repeated Content/Extended Disclaimers ======================= */}
            {/* NOTE: Content on pages 17-20 is largely a repeat or slight rephrasing of Annexures 3 and 5.
        I will create a condensed version for the template.
      */}
            <Page pageNumber={17}>
                <h3>3. [cite_start]Data Sharing & Disclosure [cite: 427]</h3>
                [cite_start]<p>With Filmmaker Teams: Contributor contact details (only for project updates)[cite: 428]. [cite_start]No sale of data to advertisers[cite: 432].</p>

                <h3>5. [cite_start]User Rights [cite: 437]</h3>
                <p>You Can: Access/correct data, request deletion (except legal/financial records), withdraw marketing consent, Port data. [cite_start]Response Time: **30 days**. [cite: 438, 440, 441, 442]</p>

                <h3>6. [cite_start]Data Security [cite: 443]</h3>
                <p>Encryption: **AES-256**. Access Controls: Role-based. [cite_start]Breach Protocol: **72-hour notification**. [cite: 444, 445, 446]</p>

                [cite_start]<h2 style={{ marginTop: '50px' }}>Disclaimer (Extended Version) [cite: 473]</h2>
                <p>1. [cite_start]**Nature of Platform**: Creative collaboration platform, not a financial investment portal (SEBI/RBI exempt)[cite: 476, 477].</p>
                <p>2. [cite_start]**No Guarantees**: Profit Sharing: Up to 25% profit potential is **not guaranteed**[cite: 481]. [cite_start]Refunds: Strictly limited[cite: 482].</p>
                <p>5. [cite_start]**Limitation of Liability**: Maximum Liability Cap: **25,000 or contribution amount (whichever lower)**[cite: 497].</p>
                <p>6. [cite_start]**Jurisdiction**: Disputes subject to **exclusive jurisdiction of Gondia Courts, Maharashtra**[cite: 499].</p>
            </Page>

            <Page pageNumber={18}>
                [cite_start]<h2>Refund Policy (Detailed) [cite: 515]</h2>
                [cite_start]<p>As a creative collaboration platform, refunds are processed based on specific conditions: [cite: 518]</p>

                <h4>Eligibility:</h4>
                <ul>
                    [cite_start]<li>No progress on the project within **140 days**: **Full refund**[cite: 523, 524].</li>
                    [cite_start]<li>Project is officially canceled: **Pro-rata refund** (after deductions)[cite: 525].</li>
                    [cite_start]<li>Contributor voluntarily withdraws: **No refund** (funds are locked)[cite: 526].</li>
                </ul>

                <h4>Processing Guidelines:</h4>
                <ul>
                    [cite_start]<li>Requests via email at **support@cinevestor.in**. [cite: 528]</li>
                    [cite_start]<li>Approved refunds processed within **15 working days**. [cite: 532]</li>
                    [cite_start]<li>Platform fees $$(1.5\%+GST)$$ and gateway fees are **non-refundable**. [cite: 533]</li>
                    [cite_start]<li>Pro-rata refunds deduct already committed costs (production, consulting). [cite: 534]</li>
                </ul>

                <h4>Non-Refundable Conditions:</h4>
                <ul>
                    [cite_start]<li>Withdrawal after funds committed to production. [cite: 544]</li>
                    [cite_start]<li>Delays due to **Force Majeure**. [cite: 545]</li>
                    [cite_start]<li>Refund requested after **180 days** from contribution. [cite: 546]</li>
                </ul>

                [cite_start]<h3 style={{ marginTop: '30px' }}>Dispute & Arbitration (Refunds) [cite: 554]</h3>
                [cite_start]<p>Unresolved refund-related disputes follow Terms of Service: **30-day mediation** and **Binding arbitration at Mumbai, Maharashtra**. [cite: 556, 557] (Note: The main clause 9 states Gondia, while the refund annexure states Mumbai for arbitration). </p>

                [cite_start]<h3 style={{ marginTop: '30px' }}>Important Note [cite: 558]</h3>
                <p>Cinevestor is responsible only for projects marked as **"Cinevestor In-House Film"** or third-party projects that opted for Cinevestor addon services (promotion, consulting, distribution, etc.). [cite_start]**Always check the project listing status before contributing.** [cite: 560, 561, 562]</p>

            </Page>

            <Page pageNumber={19}>
                <h2>Disclaimer (Continued from Page 18)</h2>
                <h3>7. Updates</h3>
                [cite_start]<p>We may revise this disclaimer -- check periodically for changes. [cite: 509]</p>
                <p>By using Cinevestor.in, you acknowledge this disclaimer. [cite_start]For queries, contact legal@cinevestor.in [cite: 510]</p>
                <p style={{ marginTop: '15px' }}>
                    [cite_start]Cinevestor is a **creative collaboration platform**, not an investment portal. [cite: 511] [cite_start]Profit-sharing (**up to 25%**) is **not guaranteed** and depends on project success. [cite: 512] [cite_start]Contributions are **non-refundable** except as per Agreement. [cite: 512]
                </p>
                [cite_start]<p>We are not liable for third-party content or project outcomes. [cite: 513] [cite_start]By using this platform, you agree to our Terms and Privacy Policy. [cite: 514]</p>

                <h2 style={{ marginTop: '30px' }}>Refund Policy</h2>
                [cite_start]<p>Last Updated: 27/06/2025 [cite: 516]</p>
                [cite_start]<p>At Cinevestor, we are committed to maintaining transparency and accountability in all contributor engagements. [cite: 517] [cite_start]As a creative collaboration platform, refunds are processed based on specific conditions outlined below: [cite: 518]</p>

                <h3>Eligibility for Refund</h3>
                <table className="contract-table">
                    <thead>
                        <tr>
                            [cite_start]<th>Scenario [cite: 521]</th>
                            [cite_start]<th>Refund Eligibility [cite: 522]</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            [cite_start]<td>No progress on the project within **140 days** from contribution [cite: 523]</td>
                            [cite_start]<td>-Full refund [cite: 524]</td>
                        </tr>
                        <tr>
                            [cite_start]<td>Project is officially canceled by Cinevestor or the Filmmaker [cite: 523]</td>
                            [cite_start]<td>-Pro-rata refund (after deductions) [cite: 525]</td>
                        </tr>
                        <tr>
                            [cite_start]<td>Contributor voluntarily withdraws from the project [cite: 526]</td>
                            [cite_start]<td>-No refund (funds are locked) [cite: 526]</td>
                        </tr>
                    </tbody>
                </table>

                <h3>Refund Processing Guidelines</h3>
                <ul>
                    [cite_start]<li>Refunds must be formally requested via email at **support@cinevestor.in**. [cite: 528]</li>
                    [cite_start]<li>Requests will be evaluated within **7 business days** of submission. [cite: 531]</li>
                    [cite_start]<li>Approved refunds will be processed within **15 working days** to the original payment method. [cite: 532]</li>
                    [cite_start]<li>**Platform fees** $$(1.5\%+GST)$$ and third-party charges (e.g., payment gateway fees) are **non-refundable**. [cite: 533]</li>
                    [cite_start]<li>Pro-rata refunds will deduct any fixed production, pre-production, or consulting costs already committed. [cite: 534]</li>
                </ul>
            </Page>

            {/* ======================= PAGE 20: Refund Policy End ======================= */}
            <Page pageNumber={20}>
                <h3>Non-Refundable Conditions</h3>
                [cite_start]<p>Refunds will not be issued under the following circumstances: [cite: 543]</p>
                <ul>
                    [cite_start]<li>Contributor requests withdrawal after funds have been **committed to production activities**. [cite: 544]</li>
                    [cite_start]<li>Delays due to **Force Majeure** (e.g., pandemic, regulatory restrictions, gateway failures). [cite: 545]</li>
                    [cite_start]<li>Refund requested after **180 days** from contribution unless otherwise legally mandated. [cite: 546]</li>
                </ul>

                <h3>How to Request a Refund</h3>
                [cite_start]<p>Send an email to **support@cinevestor.in** with the following details: [cite: 548]</p>
                <ul>
                    [cite_start]<li>Your full name and registered email [cite: 549]</li>
                    [cite_start]<li>Contribution reference ID [cite: 550]</li>
                    [cite_start]<li>Project title [cite: 551]</li>
                    [cite_start]<li>Reason for the refund request [cite: 552]</li>
                    [cite_start]<li>Attach payment proof (transaction receipt or bank statement). [cite: 553]</li>
                </ul>

                <h3 style={{ marginTop: '30px' }}>Dispute & Arbitration</h3>
                [cite_start]<p>Any unresolved refund-related disputes shall follow the dispute resolution procedure outlined in our Terms of Service, including: [cite: 555]</p>
                <ul>
                    [cite_start]<li>**30-day mediation** [cite: 556]</li>
                    [cite_start]<li>Binding arbitration at **Mumbai, Maharashtra** [cite: 557]</li>
                </ul>

                <h3 style={{ marginTop: '30px' }}>Important Note</h3>
                [cite_start]<p>Cinevestor is responsible only for: [cite: 559]</p>
                <ul>
                    [cite_start]<li>Projects marked as **"Cinevestor In-House Film"** [cite: 560]</li>
                    [cite_start]<li>Third-party filmmaker projects only if they have opted for Cinevestor addon services (promotion, consulting, distribution, etc.) [cite: 561]</li>
                </ul>
                [cite_start]<p>Always check the project listing status before contributing. [cite: 562]</p>

            </Page>

            <button onClick={handleDownloadPDF}>Download PDF</button>

        </div>
    );
};

export default ContributorContract;
